// Cache utility for Auto Replies to persist across page refreshes
import type { AutoRepliesData } from '@/hooks/useAutoReplies'

const CACHE_KEY = 'auto-replies-cache'
const CACHE_VERSION = '1.0'
const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes

interface CacheEntry {
  version: string
  data: AutoRepliesData
  cachedAt: number
  expiresAt: number
}

export const AutoRepliesCache = {
  /**
   * Save auto replies data to localStorage (optimized to reduce size)
   */
  save(data: AutoRepliesData): void {
    try {
      // Optimize data by only caching essential fields (reduce size by ~80%)
      // Sanitize reply_body to prevent JSON corruption (truncate very long fields)
      const optimizedReplies = data.replies.map(reply => ({
        id: reply.id,
        client_id: reply.client_id,
        to_email: reply.to_email,
        from_email: reply.from_email,
        subject: reply.subject ? (reply.subject.length > 500 ? reply.subject.substring(0, 500) : reply.subject) : null,
        reply_body: reply.reply_body ? (reply.reply_body.length > 2000 ? reply.reply_body.substring(0, 2000) : reply.reply_body) : null,
        campaign_name: reply.campaign_name,
        campaign_id: reply.campaign_id,
        response_category: reply.response_category,
        reply_category_type: reply.reply_category_type,
        reasoning: reply.reasoning ? (reply.reasoning.length > 1000 ? reply.reasoning.substring(0, 1000) : reply.reasoning) : null,
        created_at: reply.created_at,
        categorized_at: reply.categorized_at,
        // Exclude heavy fields: data, sent_message, full metadata
      }))

      const cacheEntry: CacheEntry = {
        version: CACHE_VERSION,
        data: {
          count: data.count,
          replies: optimizedReplies as any, // Cast to match interface
        },
        cachedAt: Date.now(),
        expiresAt: Date.now() + CACHE_DURATION,
      }
      
      // Validate JSON can be stringified before saving
      const testString = JSON.stringify(cacheEntry)
      if (!testString) {
        throw new Error('Failed to stringify cache data')
      }
      
      localStorage.setItem(CACHE_KEY, testString)
      console.log(`💾 Auto replies cached (${optimizedReplies.length} replies, optimized size)`)
    } catch (error) {
      console.error('Failed to save auto replies cache:', error)
      // If localStorage is full, try aggressive cleanup
      try {
        // Clear all old caches
        this.clear()
        this.clearOldCaches()
        
        // Try again with even more aggressive optimization (only last 100 replies)
        const limitedReplies = data.replies.slice(0, 100).map(reply => ({
          id: reply.id,
          to_email: reply.to_email,
          from_email: reply.from_email,
          response_category: reply.response_category,
          created_at: reply.created_at,
        }))
        
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          version: CACHE_VERSION,
          data: { count: data.count, replies: limitedReplies },
          cachedAt: Date.now(),
          expiresAt: Date.now() + CACHE_DURATION,
        }))
        console.log(`💾 Auto replies cached (100 most recent, minimal size)`)
      } catch (retryError) {
        console.error('Failed to save cache after clearing:', retryError)
        // Silently fail - app will work without cache
      }
    }
  },

  /**
   * Load auto replies data from localStorage
   */
  load(): AutoRepliesData | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) {
        return null
      }

      // Try to parse the cached data
      let cacheEntry: CacheEntry
      try {
        cacheEntry = JSON.parse(cached)
      } catch (parseError) {
        // If JSON parsing fails, the cache is corrupted - clear it
        console.error('❌ Corrupted cache detected (JSON parse error):', parseError)
        console.log('🗑️ Clearing corrupted cache...')
        this.clear()
        return null
      }

      // Validate cache structure
      if (!cacheEntry || !cacheEntry.data || !Array.isArray(cacheEntry.data.replies)) {
        console.error('❌ Invalid cache structure, clearing...')
        this.clear()
        return null
      }

      // Check version compatibility
      if (cacheEntry.version !== CACHE_VERSION) {
        console.log('Cache version mismatch, clearing old cache')
        this.clear()
        return null
      }

      // Check if expired
      if (Date.now() > cacheEntry.expiresAt) {
        console.log('Auto replies cache expired')
        this.clear()
        return null
      }

      const ageMinutes = Math.round((Date.now() - cacheEntry.cachedAt) / (1000 * 60))
      console.log(`✅ Auto replies loaded from cache (${cacheEntry.data.replies.length} replies, ${ageMinutes} minutes old)`)
      return cacheEntry.data
    } catch (error) {
      console.error('Failed to load auto replies cache:', error)
      // Clear corrupted cache
      this.clear()
      return null
    }
  },

  /**
   * Clear the cache
   */
  clear(): void {
    try {
      localStorage.removeItem(CACHE_KEY)
      console.log('🗑️ Auto replies cache cleared')
    } catch (error) {
      console.error('Failed to clear cache:', error)
    }
  },

  /**
   * Check if cache exists and is valid
   */
  isValid(): boolean {
    const cached = this.load()
    return cached !== null && cached.replies.length > 0
  },

  /**
   * Get cache age in milliseconds
   */
  getAge(): number | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null

      const cacheEntry: CacheEntry = JSON.parse(cached)
      return Date.now() - cacheEntry.cachedAt
    } catch {
      return null
    }
  },

  /**
   * Get cache timestamp (when it was cached)
   */
  getCachedAt(): number | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null

      const cacheEntry: CacheEntry = JSON.parse(cached)
      return cacheEntry.cachedAt
    } catch {
      return null
    }
  },

  /**
   * Clear old/stale caches from localStorage to free up space
   */
  clearOldCaches(): void {
    try {
      const keysToRemove: string[] = []
      
      // Find all cache-related keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (
          key.includes('cache') || 
          key.includes('Cache') ||
          key.includes('campaign') ||
          key.includes('client-reporting') ||
          key.includes('dashboard')
        )) {
          keysToRemove.push(key)
        }
      }
      
      // Remove old caches
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key)
        } catch (e) {
          // Ignore errors on individual removals
        }
      })
      
      if (keysToRemove.length > 0) {
        console.log(`🗑️ Cleared ${keysToRemove.length} old cache entries`)
      }
    } catch (error) {
      console.error('Failed to clear old caches:', error)
    }
  },
}

